// frontend/src/components/production-sequence/MonthMiniGrid.tsx
// Mini-calendario de un mes: cada día es un botón que alterna laborable/no laborable.
// Presentacional puro: recibe el mapa del calendario y callbacks; colorea por dayKind.
import { buildMonthCells, dayKind, type DayKind } from '@/utils/calendarUtils';

interface Props {
  year: number;
  month0: number;
  monthLabel: string;
  weekdayLabels: string[];           // 7 etiquetas (L M X J V S D)
  calendarMap: Map<string, boolean>;
  labelMap: Map<string, string>;     // fecha → etiqueta del festivo (tooltip)
  today: string;                     // "YYYY-MM-DD"
  dark: boolean;
  onToggleDay: (date: string) => void;
}

function cellClasses(kind: DayKind, dark: boolean): string {
  switch (kind) {
    case 'festivo':
      return dark ? 'bg-red-900/40 text-red-300 hover:bg-red-900/60'
                  : 'bg-red-100 text-red-700 hover:bg-red-200';
    case 'finde-laborable':
      return dark ? 'bg-emerald-900/40 text-emerald-300 hover:bg-emerald-900/60'
                  : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200';
    case 'finde':
      return dark ? 'bg-slate-800/60 text-slate-500 hover:bg-slate-700/60'
                  : 'bg-slate-100 text-slate-400 hover:bg-slate-200';
    case 'laborable':
    default:
      return dark ? 'text-slate-300 hover:bg-white/10'
                  : 'text-slate-600 hover:bg-slate-100';
  }
}

export function MonthMiniGrid({ year, month0, monthLabel, weekdayLabels, calendarMap, labelMap, today, dark, onToggleDay }: Props) {
  const cells = buildMonthCells(year, month0);
  const headTxt = dark ? 'text-slate-500' : 'text-slate-400';

  return (
    <div className={`rounded-xl border p-3 ${dark ? 'border-white/10 bg-slate-900/40' : 'border-slate-200 bg-white'}`}>
      <div className={`text-[12px] font-bold capitalize mb-2 ${dark ? 'text-slate-200' : 'text-slate-700'}`}>{monthLabel}</div>
      <div className="grid grid-cols-7 gap-0.5">
        {weekdayLabels.map((w, i) => (
          <div key={`h${i}`} className={`text-center text-[9px] font-semibold uppercase ${headTxt}`}>{w}</div>
        ))}
        {cells.map((date, i) => {
          if (!date) return <div key={`e${i}`} />;
          const kind = dayKind(date, calendarMap);
          const isToday = date === today;
          const dayNum = Number(date.slice(8, 10));
          const label = labelMap.get(date);
          return (
            <button
              key={date}
              onClick={() => onToggleDay(date)}
              title={label}
              className={`h-7 rounded-md text-[11px] tabular-nums cursor-pointer transition-colors ${cellClasses(kind, dark)} ${isToday ? 'ring-2 ring-sky-400 font-bold' : ''}`}
            >
              {dayNum}
            </button>
          );
        })}
      </div>
    </div>
  );
}
